/**
 * Apple 动效 spring 预设库（通用 · 跨主题/跨 shell）
 * ------------------------------------------------------------------
 * 把 SwiftUI（iOS 26 / macOS Tahoe 26）的命名 spring 物理参数固化为一组
 * 可复用的 framer-motion `Transition` 预设，替代各组件硬编码的 stiffness/damping。
 *
 * Apple 的动效哲学（HIG / WWDC25「Get to know the new design system · Continuity」）：
 *  - **spring 优先**：有机运动用 spring，不用线性/ease 伪造回弹；命名预设统一全应用手感。
 *  - **克制**：响应快、阻尼足、过冲极小（bouncy 才有一点 overshoot），绝不弹跳喧闹。
 *  - **连续性（continuity）**：元素在状态间 morph，而非淡入淡出瞬切（见 [[liquidSpring]] 的 morph 用法）。
 *
 * SwiftUI 命名 spring → 本库映射（视觉等价，非公式精确）：
 *  - `.snappy`  → morph / tab 切换 / 选中态（快、轻微回弹）= morph 指示器默认
 *  - `.bouncy`  → 按压 / playgful hover（明显但短的 overshoot）
 *  - `.smooth`  → 大标题折叠 / 页面过渡 / 抽屉（平静、几乎无过冲）
 *  - `.rigid`   → 开关 / 状态翻转（无回弹，干净落位）
 *  - `.gentle`  → 大面积面板 / 抽屉展开（慢收敛）
 *
 * 用法：
 *   import { liquidSpring, liquidEase } from '@/lib/motion/springs';
 *   <motion.div transition={liquidSpring.smooth} />
 *   // 或尊重 reduced-motion：
 *   <motion.div transition={withReducedMotion(liquidSpring.snappy)} />
 *
 * 通用性：纯常量 + helper，无主题/皮肤依赖；任意 shell（liquid/glass/neon…）复用同一手感。
 */
import { useReducedMotion, type Transition, type Easing } from 'framer-motion';

/** 命名 spring 预设（framer-motion stiffness/damping/mass 形式，视觉等价 SwiftUI 命名 spring）。 */
export const liquidSpring = {
  /** `.snappy` —— morph / tab / 选中态。快、轻微回弹（morph 指示器默认）。 */
  snappy: { type: 'spring', stiffness: 520, damping: 38, mass: 0.85 } satisfies Transition,
  /** `.bouncy` —— 按压 / playful 交互。明显但短促的 overshoot。 */
  bouncy: { type: 'spring', stiffness: 480, damping: 22, mass: 0.7 } satisfies Transition,
  /** `.smooth` —— 大标题折叠 / 页面过渡 / 抽屉滑动。平静、几乎无过冲。 */
  smooth: { type: 'spring', stiffness: 320, damping: 36, mass: 0.9 } satisfies Transition,
  /** `.rigid` —— 开关 / 状态翻转。无回弹，干净落位。 */
  rigid: { type: 'spring', stiffness: 600, damping: 40, mass: 0.8 } satisfies Transition,
  /** `.gentle` —— 大面积面板 / 抽屉展开。慢收敛、稳重。 */
  gentle: { type: 'spring', stiffness: 200, damping: 30, mass: 1.0 } satisfies Transition,
} as const;

/** 非 spring 的 ease 曲线（Apple 收尾减速签名；用于 spring 不合适的色彩/透明度过渡）。 */
export const liquidEase = {
  /** easeOutExpo —— Apple 通用收尾（位移/缩放）。 */
  outExpo: [0.16, 1, 0.3, 1] as [number, number, number, number] satisfies Easing,
  /** easeOutQuint —— 温润收尾（ink 家族亦用）。 */
  outQuint: [0.22, 1, 0.36, 1] as [number, number, number, number] satisfies Easing,
  /** easeOutBack —— 轻微过冲收尾（小元素入场）。 */
  outBack: [0.34, 1.56, 0.64, 1] as [number, number, number, number] satisfies Easing,
} as const;

/**
 * 尊重 prefers-reduced-motion：开启时把任意 Transition 降级为瞬切（duration:0）。
 * 与在组件内自行判 `useReducedMotion()` 等价，但集中一处避免遗漏。
 */
export function withReducedMotion(transition: Transition): Transition {
  // 运行时读 media query（与 framer-motion useReducedMotion 同源），SSR/首屏安全。
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return { duration: 0 };
  }
  return transition;
}

/** Hook 形式：组件内按 reduced-motion 在 spring 与瞬切间切换（响应系统设置变化）。 */
export function useLiquidTransition(transition: Transition): Transition {
  const reduce = useReducedMotion();
  return reduce ? { duration: 0 } : transition;
}

export default liquidSpring;
